import { useState } from 'react';
import { useIntl } from 'react-intl';
import { targetingConstants } from 'sonata-platform-components';
import { Toast, type ValidationError } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { DO_NOT_DISTURB_TARGETING_SECTION_KEY } from '../common/common';
import { SelectionableSectionWithConfirmation } from '../common/SelectionableSectionWithConfirmation';
import { ContextualTargetingDistribution } from './ContextualTargetingDistribution/ContextualTargetingDistribution';
import { getContentCategoryTags } from './helpers/getSectionHeaderTags.helper';
import { IABCategoriesSelector } from './IABCategoriesSelector/IABCategoriesSelector';
import { IASCategoriesSelector } from './IASCategoriesSelector/IASCategoriesSelector';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';

import './contentCategories.styles.scss';

const { DEFAULT_CONTEXTUAL_TARGETING } = targetingConstants;

type State = {
  showErrorToast: boolean;
};

type Props = {
  baseCurrency: BaseCurrencyInterface;
  error?: ValidationError;
  totalBudget: number;
};

function ContentCategories({ baseCurrency, error, totalBudget }: Props) {
  const { setProp, setUnknownIncluded, resetSection, targeting, user, userRoles } = useTargetingContext();
  const { contextualTargeting } = targeting;

  const hasIABPermission = checkUserResourcePermissions('contentCategories', user, userRoles);
  const hasIASPermission = checkUserResourcePermissions('iasCategories', user, userRoles);

  const isIABEnabled = Boolean(contextualTargeting?.iabCategories?.enabled);
  const isIASEnabled = Boolean(contextualTargeting?.iasCategories?.enabled);

  const { formatMessage } = useIntl();

  const [{ showErrorToast }, setState] = useState<State>({
    showErrorToast: true
  });

  const handleIABSectionChange = () => {
    setProp('contextualTargeting', {
      ...contextualTargeting,
      iabCategories: DEFAULT_CONTEXTUAL_TARGETING.contextualTargeting.iabCategories
    });
    setUnknownIncluded('contentCategories', DEFAULT_CONTEXTUAL_TARGETING.unknownIncluded.contentCategories);
  };

  const handleIASSectionChange = () => {
    setProp('contextualTargeting', {
      ...contextualTargeting,
      iasCategories: DEFAULT_CONTEXTUAL_TARGETING.contextualTargeting.iasCategories
    });
    setUnknownIncluded(
      'thirdPartyContentCategories',
      DEFAULT_CONTEXTUAL_TARGETING.unknownIncluded.thirdPartyContentCategories
    );
  };

  const handleResetContentCategories = () => {
    resetSection('contentCategories');
  };

  const isSectionOpened = isIABEnabled || isIASEnabled;
  const contentCategoryTags = getContentCategoryTags(isIABEnabled, isIASEnabled);

  return (
    <div className="contentCategoriesSection">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        hasErrors={Boolean(error?.message)}
        onReset={handleResetContentCategories}
        preffixId="contentCategories"
        tags={contentCategoryTags}
        title={formatMessage({
          defaultMessage: 'Content Categories',
          id: 'targeting.contentCategories.title'
        })}
        tooltipText={formatMessage({
          defaultMessage: 'Filter the exact type of content surrounding your ad to ensure high contextual relevance.',
          id: 'targeting.contentCategories.tooltip'
        })}
      >
        <div className="contentCategoriesSection__container">
          {error?.message && showErrorToast && (
            <Toast
              autoClose={false}
              className="settings__errorToast"
              closeToast={() => setState(prevState => ({ ...prevState, showErrorToast: false }))}
              icon="fal fa-exclamation-triangle"
              text={formatMessage({
                defaultMessage: 'Please fix the errors below to proceed',
                id: 'ioSettings.fixErrorsMessage'
              })}
              type="danger"
            />
          )}
          <div className="contentCategoriesSection__boxBody">
            {hasIABPermission && (
              <SelectionableSectionWithConfirmation
                id={'iab-category-section'}
                isSectionEnabled={isIABEnabled}
                label={formatMessage({
                  defaultMessage: 'IAB Category',
                  id: 'targeting.contentCategories.iab.label'
                })}
                modalConfirm={{
                  storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
                }}
                onSectionEnabledChange={handleIABSectionChange}
              >
                <IABCategoriesSelector />
              </SelectionableSectionWithConfirmation>
            )}
            {hasIASPermission && (
              <SelectionableSectionWithConfirmation
                id={'ias-category-section'}
                isSectionEnabled={isIASEnabled}
                label={formatMessage({
                  defaultMessage: 'IAS Category',
                  id: 'targeting.contentCategories.ias.label'
                })}
                modalConfirm={{
                  storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
                }}
                onSectionEnabledChange={handleIASSectionChange}
              >
                <IASCategoriesSelector />
              </SelectionableSectionWithConfirmation>
            )}
            <ContextualTargetingDistribution baseCurrency={baseCurrency} totalBudget={totalBudget} />
          </div>
        </div>
      </FormExpandableSection>
    </div>
  );
}

export default ContentCategories;
